import React from "react";
import { FaShieldAlt, FaTimesCircle, FaWallet } from "react-icons/fa";
import BottomNav from "../../components/BottomNav";
import HeaderWithBack from "../../components/HeaderWithBack";
import Loading from "../../components/Loading";
import { useBenefits } from "../../hooks/useBenefits";
import { useAuthStore } from "../../stores/authStores";
import { formatRupiah } from "../../utils/formatPrice";

interface Benefit {
  id: number;
  code: string;
  description: string;
  used: number;
  limit: number;
  remaining: number;
}

interface BenefitData {
  eligible: boolean;
  message?: string;
  member?: {
    name: string;
    member_id: string;
    policy_id: string;
  };
  total_premi?: number;
  limit_rules?: number;
  benefits?: Benefit[];
}

const BENEFIT_ICONS: Record<string, string> = {
  OPCONS1: "🩺",
  OPCONS2: "👨‍⚕️",
  OPDIAG1: "🔬",
  OPDIAG2: "🩻",
  OPADM1:  "📋",
  OPTIN1:  "💉",
};

const BenefitList: React.FC = () => {
  const token = useAuthStore((state) => state.token);
  const { data, isLoading } = useBenefits(token);

  if (isLoading) {
    return (
      <>
        <HeaderWithBack title="Benefit Saya" />
        <div className="mx-4 mt-4 bg-white rounded-2xl shadow p-6 flex justify-center">
          <Loading />
        </div>
      </>
    );
  }

  if (!data?.eligible) {
    return (
      <>
        <HeaderWithBack title="Benefit Saya" />
        <div className="mx-4 mt-4 bg-white rounded-2xl shadow p-4 flex items-center gap-3">
          <FaTimesCircle className="text-gray-400 text-xl flex-shrink-0" />
          <div>
            <p className="font-semibold text-gray-600 text-sm">Benefit Asuransi</p>
            <p className="text-xs text-gray-400 mt-0.5">
              {data?.message ?? "Anda tidak terdaftar dalam program asuransi"}
            </p>
          </div>
        </div>
      </>
    );
  }

  const usedTotal = data?.benefits?.reduce((sum: number, b: Benefit) => sum + b.used, 0) ?? 0;
  const limitRules = data?.limit_rules ?? 0;
  const usedPercent = limitRules > 0 ? Math.min((usedTotal / limitRules) * 100, 100) : 0;

  return (
    <div style={{ fontFamily: 'Arial, sans-serif', minHeight: '100vh', paddingBottom: '60px' }}>
        <HeaderWithBack title="Benefit Saya" />
        <div className="mx-4 mt-4">
            <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl shadow-md overflow-hidden">
                <div className="px-4 pt-4 pb-3 flex items-start gap-3">
                    <div className="bg-white/20 rounded-full p-2 flex-shrink-0">
                        <FaShieldAlt className="text-white text-lg" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-white/70 text-xs tracking-widest uppercase font-semibold">
                        Benefit Asuransi
                        </p>
                        <p className="text-white font-bold text-base leading-tight mt-0.5 truncate">
                        {data.member?.name}
                        </p>
                        <div className="flex gap-3 mt-1">
                        <span className="text-white/70 text-xs">
                            ID: <span className="text-white font-medium">{data.member?.member_id}</span>
                        </span>
                        <span className="text-white/70 text-xs">
                            Polis: <span className="text-white font-medium">{data.member?.policy_id}</span>
                        </span>
                        </div>
                    </div>
                  </div>

                  <div className="px-4 pb-4">
                    <div className="bg-white/10 rounded-xl p-3">
                        <div className="flex justify-between items-center mb-1.5">
                          <span className="text-white/80 text-xs font-medium flex items-center gap-1">
                              <FaWallet className="text-xs" /> Sisa Limit
                          </span>
                          <span className="text-white text-xs font-bold">
                              {formatRupiah(limitRules - usedTotal)} / {formatRupiah(limitRules)}
                          </span>
                        </div>
                        <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                          <div
                              className="h-full bg-white rounded-full transition-all duration-700"
                              style={{ width: `${usedPercent}%` }}
                          />
                        </div>
                        <p className="text-white/60 text-xs mt-1 text-right">
                          Terpakai {usedPercent.toFixed(1)}%
                        </p>
                    </div>
                </div>
            </div>

            <div className="mt-3 space-y-2">
                {data.benefits?.map((benefit: Benefit) => {
                const pct  = benefit.limit > 0
                    ? Math.min((benefit.used / benefit.limit) * 100, 100)
                    : 0;
                const icon = BENEFIT_ICONS[benefit.code] ?? "✅";

                return (
                    <div
                    key={benefit.id}
                    className="bg-white rounded-xl shadow-sm border border-gray-100 p-3"
                    >
                      <div className="flex items-start gap-3">
                          <span className="text-xl flex-shrink-0 mt-0.5">{icon}</span>
                          <div className="flex-1 min-w-0">
                            <div className="flex justify-between items-start gap-2">
                                <p className="text-sm font-semibold text-gray-800 leading-tight">
                                  {benefit.description}
                                </p>
                                <span className="text-xs text-teal-600 font-bold bg-teal-50 px-2 py-0.5 rounded-full flex-shrink-0">
                                  {benefit.code}
                                </span>
                            </div>

                            <div className="mt-2">
                                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                <div
                                    className={`h-full rounded-full transition-all duration-700 ${
                                    pct >= 90 ? "bg-red-400" : pct >= 60 ? "bg-amber-400" : "bg-emerald-400"
                                    }`}
                                    style={{ width: `${pct}%` }}
                                />
                                </div>
                                <div className="flex justify-end text-xs mt-1">
                                  <span className="text-gray-400">
                                      Terpakai: <span className="text-gray-600 font-medium">{formatRupiah(benefit.used)}</span>
                                  </span>
                                </div>
                            </div>
                          </div>
                      </div>
                    </div>
                );
                })}
            </div>

            <BottomNav />
        </div>
    </div>
  );
};

export default BenefitList;